import type { Address } from "./address";
import { associationIdName, fetchGet, levelsOfTargetsStrings } from "./common";
import type { PoliticianIdentifier, PoliticianScore, Profile } from "./officials";

export interface Office {
  candidates: Candidate[];
  office: string;
  profiles?: Profile[];
}

export interface Election {
  election: string;
  description?: string;
  displayCategory: string;
  generalElectionDate?: string;
  primaryElectionDate?: string;
  primaryRunOffDate?: string;

  year: number;
  month: string;
  day: number;
  offices: Office[];
  passed: boolean;
}

export interface CandidateGrouping {
  office: string;
  candidates: Candidate;
}

export interface Candidate {
  id: number;
  name: string;
  party: string;
  status: string;
}

export interface VotingResources {
  AdditionalResourcesUrl: string;
  PollingLocationUrl: string;
  VoterRegistrationUrl: string;
  Elections: Election[];
}

export const getVotingResources = async (address: Address, state: string) => {
  const encodedAddress = encodeURIComponent(JSON.stringify(address));
  const url = `VotingResources?association=${associationIdName}&address=${encodedAddress}&state=${state}`;
  const response = await fetchGet(url) as VotingResources;
  return response;
}

export const getProfiles = async (candidates: PoliticianIdentifier[]) => {
  const url = `Profiles?filter=${encodeURIComponent(JSON.stringify({
    identifiers: candidates
  }))}&association=${associationIdName}`;
  const response = await fetchGet(url) as Profile[];
  return response;
}

export const getProfile = async (id: number) => {
  const url = `Profiles/${id}?type=X&association=${associationIdName}`;
  const response = await fetchGet(url) as Profile;
  return response;
}

export const getPoliticianScore = async (id: number) => {
  const url = `Scorecards/Score/${id}?association=${associationIdName}`;
  const response = await fetchGet(url,true) as PoliticianScore;
  return response;
}

export const getTextColorByParty = (party: string): string => {
  const mappedParties: any = {
    Republican: 'text-vv-republican-party',
    Democratic: 'text-vv-democrat-party',
    Democrat: 'text-vv-democrat-party',
    Independent: 'text-vv-independent-party',
    Libertarian: 'text-vv-libertarian-party',
    "Green Party": 'text-vv-green-party',
    Other: 'text-vv-other-party',
  }
  
  if (party in mappedParties)
    return mappedParties[party];
  else
    return mappedParties['Other'];
}

export function organizeProfileIntoKeyValuePairs(profile: Profile): { [key: string]: any } {
  const occupationKey: string = 'Occupation';
  let dictionary: { [key: string]: any } = {};
  const partyAndState = extractPartyFromProfile(profile);
  const nameWithoutTitle = partyAndState.titleAndName.replaceAll(
      `${profile.personName?.title ?? ''}`,
      ``
  );
  dictionary['displayName'] = nameWithoutTitle.replaceAll('Dear', '');
  dictionary['title'] = profile.personName?.title ?? '';
  dictionary['id'] = profile.id;
  dictionary['photoUrl'] = profile.photoUrl;
  dictionary['party'] = partyAndState.party;
  dictionary[levelsOfTargetsStrings.state] = partyAndState.state;
  for (const section of profile.sections) {
    for (const property of section.properties) {
      if (property.name === occupationKey) property.value = property.value.replaceAll(`; `, `\n`);
      if (dictionary.hasOwnProperty(property.name))
        dictionary[property.name].push(property.value);
      else dictionary[property.name] = [property.value];
    }
  }
  return dictionary;
}

export function extractPartyFromProfile(profile: Profile): any {
  const regex = /^(.*) \(([^-()]+)(?:-([^()]+))?\)$/;
  const match = profile.displayName.match(regex);

  if (!match) return null;

  const [_, titleAndName, party, state] = match;
  return {
    titleAndName: titleAndName.trim(),
    party: party.trim(),
    state: state ? state.trim() : null
  };
}